import { ref, get, database } from "../config/firebase";
import { DataTypeVerbete as Verbete } from "../hooks/sinalario";

export interface Exercise {
  id: string;
  type: "video_to_text" | "text_to_video" | "memory_game" | "word_completion";
  question: string;
  correctAnswer: string;
  options: string[];
  videoUrl?: string;
  explanation?: string;
  points: number;
  gameWords?: string[];
  difficulty: "easy" | "medium" | "hard";
}

export interface Lesson {
  id: string;
  title: string;
  description: string;
  moduleId: string;
  order: number;
  exercises: Exercise[];
  isLocked: boolean;
  isCompleted: boolean;
  estimatedTime: number; // in minutes
  thumbnailUrl?: string;
}

export interface Module {
  id: string;
  title: string;
  description: string;
  order: number;
  icon?: string;
  color?: string;
  lessons: Lesson[];
}

/* eslint-disable class-methods-use-this */
class LessonsService {
  private readonly possiblePaths = [
    "lessons/modules",
    "lessons",
    "modules",
    "aulas/modulos",
    "aulas",
  ];

  async getModules(verbetes: Verbete[]): Promise<Module[]> {
    try {
      if (!database) return this.generateModulesFromWords(verbetes);

      let modulesData: Module[] = [];
      let found = false;

      /* eslint-disable no-await-in-loop, no-restricted-syntax */
      for (const path of this.possiblePaths) {
        const modulesRef = ref(database, path);
        const snapshot = await get(modulesRef);

        if (snapshot.exists()) {
          const data = snapshot.val();
          modulesData = this.processFirebaseData(data, verbetes);
          found = true;
          break;
        }
      }
      /* eslint-enable no-await-in-loop, no-restricted-syntax */

      if (!found || modulesData.length === 0) {
        return this.generateModulesFromWords(verbetes);
      }

      return modulesData;
    } catch (error) {
      console.error("Error fetching modules:", error);
      return this.generateModulesFromWords(verbetes);
    }
  }

  private processFirebaseData(data: any, verbetes: Verbete[]): Module[] {
    let rawModules: any[] = [];

    if (data.modules) {
      rawModules = Object.entries(data.modules).map(
        ([id, mod]: [string, any]) => ({ ...mod, id })
      );
    } else if (Array.isArray(data)) {
      rawModules = data.map((mod, index) => ({
        ...mod,
        id: mod.id || `module-${index}`,
      }));
    } else {
      rawModules = Object.entries(data).map(([id, mod]: [string, any]) => ({
        ...mod,
        id,
      }));
    }

    return rawModules
      .map((mod) => ({
        ...mod,
        lessons: this.processLessons(mod.lessons, mod.id, verbetes),
      }))
      .sort((a, b) => (a.order || 0) - (b.order || 0));
  }

  private processLessons(
    lessonsData: any,
    moduleId: string,
    verbetes: Verbete[]
  ): Lesson[] {
    if (!lessonsData) return [];

    let lessonsArr: any[] = [];
    if (Array.isArray(lessonsData)) {
      lessonsArr = lessonsData;
    } else {
      lessonsArr = Object.entries(lessonsData).map(
        ([id, lesson]: [string, any]) => ({ ...lesson, id })
      );
    }

    return lessonsArr
      .map((lesson) => ({
        id: lesson.id,
        title: lesson.title || lesson.nome || "Lição",
        description: lesson.description || lesson.descricao || "",
        moduleId,
        order: lesson.order || 0,
        isLocked: lesson.isLocked || false,
        isCompleted: lesson.isCompleted || false,
        estimatedTime: lesson.estimatedTime || 10,
        thumbnailUrl: lesson.thumbnailUrl || "",
        exercises: this.processExercises(
          lesson.exercises || lesson.exercícios,
          verbetes
        ),
      }))
      .sort((a, b) => a.order - b.order);
  }

  private processExercises(
    exercisesData: any,
    verbetes: Verbete[]
  ): Exercise[] {
    if (!exercisesData) return [];

    let exArr: any[] = [];
    if (Array.isArray(exercisesData)) {
      exArr = exercisesData;
    } else {
      exArr = Object.entries(exercisesData).map(([id, ex]: [string, any]) => ({
        ...ex,
        id,
      }));
    }

    return exArr.map((ex) => {
      const exercise: Exercise = {
        id: ex.id || `ex-${Math.random().toString(36).substr(2, 9)}`,
        type: ex.type || "video_to_text",
        question: ex.question || "",
        correctAnswer: ex.correctAnswer || "",
        options: ex.options || [],
        videoUrl: ex.videoUrl || "",
        explanation: ex.explanation || "",
        points: ex.points || 10,
        gameWords: ex.gameWords || [],
        difficulty: ex.difficulty || "easy",
      };

      // Enrich with verbete data if needed
      if ((ex.wordId || ex.palavra) && verbetes.length > 0) {
        const word = verbetes.find(
          (v) => v.uid === ex.wordId || v.palavra === ex.palavra
        );
        if (word) {
          if (!exercise.videoUrl) exercise.videoUrl = word.video;
          if (!exercise.correctAnswer) exercise.correctAnswer = word.palavra;
          if (exercise.options.length === 0) {
            const distractors = verbetes
              .filter((v) => v.uid !== word.uid)
              .sort(() => Math.random() - 0.5)
              .slice(0, 3)
              .map((v) => v.palavra);
            exercise.options = [word.palavra, ...distractors].sort(
              () => Math.random() - 0.5
            );
          }
        }
      }

      return exercise;
    });
  }

  private generateModulesFromWords(verbetes: Verbete[]): Module[] {
    if (!verbetes || verbetes.length === 0) return [];

    const modules: Module[] = [];

    // Module 1: Alphabet Basics
    const alphabetWords = verbetes
      .filter((v) => v.categoria?.toLowerCase() === "alfabeto")
      .slice(0, 10);
    if (alphabetWords.length > 0) {
      modules.push({
        id: "module-alphabet",
        title: "Alfabeto Manual",
        description: "Aprenda as letras do alfabeto em Libras",
        order: 1,
        icon: "keyboard",
        color: "#5F33E1",
        lessons: this.createDynamicLessons(
          alphabetWords,
          "module-alphabet",
          "Letras"
        ),
      });
    }

    // Module 2: Greetings
    const greetingTerms = [
      "olá",
      "oi",
      "bom dia",
      "boa tarde",
      "boa noite",
      "obrigado",
      "obrigada",
      "por favor",
    ];
    const greetingWords = verbetes
      .filter((v) =>
        greetingTerms.some((term) => v.palavra.toLowerCase().includes(term))
      )
      .slice(0, 10);

    if (greetingWords.length > 0) {
      modules.push({
        id: "module-greetings",
        title: "Cumprimentos",
        description: "Aprenda a saudar as pessoas em Libras",
        order: 2,
        icon: "hand",
        color: "#EC4899",
        lessons: this.createDynamicLessons(
          greetingWords,
          "module-greetings",
          "Saudações"
        ),
      });
    }

    // Module 3: Numbers
    const numberWords = verbetes
      .filter(
        (v) =>
          v.categoria?.toLowerCase() === "números" ||
          v.categoria?.toLowerCase() === "numeros"
      )
      .slice(0, 10);
    if (numberWords.length > 0) {
      modules.push({
        id: "module-numbers",
        title: "Números",
        description: "Aprenda a contar em Libras",
        order: 3,
        icon: "grid",
        color: "#10B981",
        lessons: this.createDynamicLessons(
          numberWords,
          "module-numbers",
          "Contagem"
        ),
      });
    }

    // Fallback: General Module if others are small
    if (modules.length === 0) {
      modules.push({
        id: "module-general",
        title: "Introdução à Libras",
        description: "Primeiros passos no aprendizado de sinais",
        order: 1,
        icon: "book",
        color: "#8B5CF6",
        lessons: this.createDynamicLessons(
          verbetes.slice(0, 15),
          "module-general",
          "Iniciação"
        ),
      });
    }

    return modules;
  }

  private createDynamicLessons(
    words: Verbete[],
    moduleId: string,
    prefix: string
  ): Lesson[] {
    const lessons: Lesson[] = [];
    const wordsPerLesson = 5;

    for (let i = 0; i < words.length; i += wordsPerLesson) {
      const lessonWords = words.slice(i, i + wordsPerLesson);
      const lessonPart = i / wordsPerLesson + 1;
      const lessonId = `${moduleId}-lesson-${lessonPart}`;

      lessons.push({
        id: lessonId,
        title: `${prefix} - Parte ${lessonPart}`,
        description: `Aprenda ${lessonWords.length} sinais fundamentais.`,
        moduleId,
        order: lessonPart,
        isLocked: i > 0,
        isCompleted: false,
        estimatedTime: 10,
        exercises: this.generateExercises(lessonWords, lessonId, words),
      });
    }

    return lessons;
  }

  private generateExercises(
    lessonWords: Verbete[],
    lessonId: string,
    allWords: Verbete[]
  ): Exercise[] {
    const exercises: Exercise[] = [];

    lessonWords.forEach((word, index) => {
      // Exercise 1: Video to Text
      const distractors = allWords
        .filter((v) => v.uid !== word.uid)
        .sort(() => Math.random() - 0.5)
        .slice(0, 3)
        .map((v) => v.palavra);

      exercises.push({
        id: `${lessonId}-ex-${index}-v2t`,
        type: "video_to_text",
        question: "Qual é o sinal mostrado no vídeo?",
        correctAnswer: word.palavra,
        options: [word.palavra, ...distractors].sort(() => Math.random() - 0.5),
        videoUrl: word.video,
        points: 10,
        difficulty: "easy",
        explanation: `O sinal mostrado é "${word.palavra}".`,
      });

      // Exercise 2: Text to Video (if video exists)
      if (word.video) {
        const videoDistractors = allWords
          .filter((v) => v.uid !== word.uid && v.video)
          .sort(() => Math.random() - 0.5)
          .slice(0, 2)
          .map((v) => v.video);

        if (videoDistractors.length >= 2) {
          exercises.push({
            id: `${lessonId}-ex-${index}-t2v`,
            type: "text_to_video",
            question: `Qual vídeo corresponde à palavra "${word.palavra}"?`,
            correctAnswer: word.video,
            options: [word.video, ...videoDistractors].sort(
              () => Math.random() - 0.5
            ) as string[],
            points: 10,
            difficulty: "easy",
            explanation: `Este é o vídeo correto para "${word.palavra}".`,
          });
        }
      }
    });

    return exercises;
  }
}

export const lessonsService = new LessonsService();
